import * as React from 'react';
import {useRef, useState, useEffect} from 'react';
import {useButton, useFocusRing, useCheckbox} from 'react-aria';
import {useOverlayTriggerState, useToggleState} from 'react-stately';
import {ChevronUpDownIcon, MagnifyingGlassIcon} from '@heroicons/react/20/solid';
import {XMarkIcon} from '@heroicons/react/16/solid';
import {useTranslation} from 'react-i18next';
import {useHover} from '@react-aria/interactions';
import clsx from 'clsx';
import {FieldButton} from "../DateRangePicker";
import {InputSetToNull} from "../Icons";
import {Popover} from "../Popover";
import {map} from "lodash";

export interface MultiSelectItem {
    id: string | number;
    name: string;
}

interface MultiSelectProps {
    label?: string;
    placeholder?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    items: MultiSelectItem[];
    selectedKeys: (string | number)[];
    onSelectionChange: (keys: (string | number)[]) => void;
    error?: string;
    variant?: 'outlined' | 'embedded';
    className?: string;
    allowsSearch?: boolean;
    searchPlaceholder?: string;
    fullWidth?: boolean;
    maxVisibleSelections?: number;
}

// ─── Individual option row ────────────────────────────────────────────────────

interface OptionProps {
    item: MultiSelectItem;
    isSelected: boolean;
    onToggle: (id: string | number) => void;
    isDisabled?: boolean;
}

function Option({item, isSelected, onToggle, isDisabled}: OptionProps) {
    const checkboxRef = useRef<HTMLInputElement>(null);
    const toggleState = useToggleState({isSelected, onChange: () => onToggle(item.id)});
    const {inputProps} = useCheckbox(
        {isSelected, isDisabled, 'aria-label': item.name, onChange: () => onToggle(item.id)},
        toggleState,
        checkboxRef,
    );

    return (
        <li>
            <label
                className={clsx(
                    'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer select-none',
                    'hover:bg-gray-100 transition-colors',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                )}
            >
                <input
                    {...inputProps}
                    ref={checkboxRef}
                    type="checkbox"
                    className="w-4 h-4 rounded flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    style={{accentColor: 'black'}}
                />
                <span className="flex-1 truncate text-gray-900">{item.name}</span>
            </label>
        </li>
    );
}

// ─── Selected value chip ──────────────────────────────────────────────────────

interface ChipProps {
    label: string;
    onRemove: () => void;
    isDisabled?: boolean;
}

function Chip({label, onRemove, isDisabled}: ChipProps) {
    const ref = useRef<HTMLButtonElement>(null);
    const {buttonProps} = useButton(
        {onPress: onRemove, isDisabled, 'aria-label': `Remove ${label}`},
        ref,
    );

    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-700 max-w-[140px]">
            <span className="truncate">{label}</span>
            {!isDisabled && (
                <button
                    {...buttonProps}
                    ref={ref}
                    className="flex-shrink-0 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-pink-500"
                >
                    <XMarkIcon className="w-3 h-3 text-gray-500"/>
                </button>
            )}
        </span>
    );
}

// ─── MultiSelect ─────────────────────────────────────────────────────────────

export function MultiSelect(props: MultiSelectProps) {
    const {t} = useTranslation();

    const {
        label,
        placeholder,
        isRequired,
        isDisabled = false,
        isReadOnly = false,
        items,
        selectedKeys,
        onSelectionChange,
        error,
        variant = 'outlined',
        className,
        allowsSearch = true,
        searchPlaceholder,
        fullWidth = false,
        maxVisibleSelections = 3
    } = props;

    const state = useOverlayTriggerState({});
    const [searchValue, setSearchValue] = useState('');
    const [triggerWidth, setTriggerWidth] = useState(0);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const {buttonProps} = useButton(
        {onPress: () => !isDisabled && !isReadOnly && state.toggle()},
        triggerRef,
    );
    const {focusProps, isFocusVisible} = useFocusRing();
    const {hoverProps, isHovered} = useHover({});

    const isEmbedded = variant === 'embedded';
    const variantClass = `field-${variant}`;
    const selectedSet = new Set(selectedKeys.map(String));

    const filteredItems = allowsSearch && searchValue.trim()
        ? items.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()))
        : items;

    const selectedItems = items.filter(item => selectedSet.has(String(item.id)));
    const visibleSelections = selectedItems.slice(0, maxVisibleSelections);
    const hiddenSelectionsCount = selectedItems.length - visibleSelections.length;

    const handleToggle = (id: string | number) => {
        const key = String(id);
        const next = selectedSet.has(key)
            ? selectedKeys.filter(k => String(k) !== key)
            : [...selectedKeys, id];
        onSelectionChange(next);
    };

    const handleRemoveChip = (id: string | number) => {
        onSelectionChange(selectedKeys.filter(k => String(k) !== String(id)));
    };

    const handleClearAll = () => onSelectionChange([]);

    // Focus search on open
    useEffect(() => {
        if (state.isOpen && allowsSearch) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [state.isOpen, allowsSearch]);

    // Clear search on close
    useEffect(() => {
        if (!state.isOpen) setSearchValue('');
    }, [state.isOpen]);

    // Track trigger width for popover
    useEffect(() => {
        const update = () => {
            if (triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [state.isOpen]);

    return (
        <div
            className={clsx(
                'inline-flex flex-col relative min-w-[180px]',
                fullWidth && 'w-full',
                className,
            )}
        >
            {/* Label */}
            {label && (
                <label className={`field-label ${isDisabled ? 'field-disabled' : ''}`}>
                    {label}
                    {isRequired && <span className="field-required">*</span>}
                </label>
            )}

            {/* Trigger */}
            <button
                {...buttonProps}
                {...hoverProps}
                {...focusProps}
                ref={triggerRef}
                disabled={isDisabled}
                className={clsx(
                    'field-base',
                    variantClass,
                    'items-start justify-between min-h-[38px]',
                    isFocusVisible ? 'border-pink-500' : 'border-gray-300',
                    state.isOpen ? 'bg-gray-100' : 'bg-inherit',
                    isHovered && 'field-hovered',
                    isDisabled && 'field-disabled',
                    isEmbedded && state.isOpen && 'field-pressed',
                    error && 'border-red-500',
                )}
            >
                {/* Selected chips or placeholder */}
                <div className="flex flex-wrap gap-1 flex-1 min-w-0 py-0.5">
                    {selectedItems.length > 0 ? (
                        <>
                            {visibleSelections.map(item => (
                                <Chip
                                    key={item.id}
                                    label={item.name}
                                    isDisabled={isDisabled || isReadOnly}
                                    onRemove={() => handleRemoveChip(item.id)}
                                />
                            ))}

                            {hiddenSelectionsCount > 0 && (
                                <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-200 text-xs text-gray-600">
                                    +{hiddenSelectionsCount}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className={`${variantClass} sm:text-sm/6 text-gray-400`}>
                    {placeholder ?? t('components.multiSelect.placeholder', 'Option auswählen')}
                </span>
                    )}
                </div>

                {/* Right controls */}
                <div className="flex items-center space-x-1 flex-shrink-0 self-center ml-1">
                    {!isDisabled && !isReadOnly && selectedItems.length > 0 && (
                        <FieldButton onPress={handleClearAll} aria-label="Clear all">
                            <InputSetToNull className="text-gray-700 w-3.5 h-3.5"/>
                        </FieldButton>
                    )}

                    {!isReadOnly && (
                        <ChevronUpDownIcon
                            className={`w-5 h-5 flex-shrink-0 ${isFocusVisible ? 'text-pink-500' : 'text-gray-500'}`}
                        />
                    )}

                    {isReadOnly && (
                        <span className="text-xs text-gray-500 rounded-md bg-gray-200 px-2 py-1">
                            {t('components.select.readOnly')}
                        </span>
                    )}
                </div>
            </button>

            {/* Error */
            }
            {
                error && <p className="field-error mt-1">{error}</p>
            }

            {/* Dropdown */
            }
            {
                !isReadOnly && state.isOpen && (
                    <Popover state={state} triggerRef={triggerRef} placement="bottom start">
                        <div
                            className="flex flex-col bg-white rounded-md"
                            style={{minWidth: triggerWidth ? `${triggerWidth}px` : 'auto'}}
                        >
                            {/* Search */}
                            {allowsSearch && (
                                <div className="p-2 border-b border-gray-200 sticky top-0 z-10 bg-white">
                                    <div className="relative">
                                        <MagnifyingGlassIcon
                                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                                        <input
                                            ref={searchRef}
                                            type="text"
                                            value={searchValue}
                                            onChange={e => setSearchValue(e.target.value)}
                                            placeholder={searchPlaceholder ?? t('components.select.searchPlaceholder')}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            onClick={e => e.stopPropagation()}
                                            onKeyDown={e => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Options */}
                            <ul className="overflow-auto py-1 max-h-60" role="listbox" aria-multiselectable="true">
                                {filteredItems.length === 0 ? (
                                    <li className="px-3 py-8 text-center text-sm text-gray-500">
                                        {searchValue
                                            ? t('components.select.noResults')
                                            : t('components.select.noItems')}
                                    </li>
                                ) : (
                                    filteredItems.map(item => (
                                        <Option
                                            key={item.id}
                                            item={item}
                                            isSelected={selectedSet.has(String(item.id))}
                                            onToggle={handleToggle}
                                            isDisabled={isDisabled}
                                        />
                                    ))
                                )}
                            </ul>

                            {/* Footer count */}
                            {selectedItems.length > 0 && (
                                <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-500">
                                        {selectedItems.length} {t('components.multiSelect.selected', 'ausgewählt')}
                                    </span>
                                    <button
                                        onClick={handleClearAll}
                                        className="text-xs bg-transparent text-pink-600 hover:text-pink-700 font-medium focus:outline-none"
                                    >
                                        {t('components.multiSelect.clearAll', 'Alle entfernen')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </Popover>
                )
            }
        </div>
    )
        ;
}